import React from 'react';
import { 
  FiCheck, 
  FiWifi, 
  FiWind, 
  FiCoffee, 
  FiTv, 
  FiMaximize, 
  FiShield, 
  FiDroplet, 
  FiHeart, 
  FiLock,
  FiArchive,
  FiCloudRain
} from 'react-icons/fi';
import PageTransition from '../components/PageTransition';
import SafeIcon from '../common/SafeIcon';

const Stay = () => {
  const image1 = "https://media-manager-c.questera.ai/greta-media/51a91a93706672e53ae444a23254479bc2f5b1a8165fab41d806a43d2b869fef8268d071e9340f739bfeb737c7b371ae/images/aW1hZ2UvanBlZw==/ed97c67b1eaae9a55591fe6f74b16510.jpeg";
  const image2 = "https://media-manager-c.questera.ai/greta-media/51a91a93706672e53ae444a23254479bc2f5b1a8165fab41d806a43d2b869fef8268d071e9340f739bfeb737c7b371ae/images/aW1hZ2UvanBlZw==/78c6a8d315e321ead0b34ce434f34a27.jpg";
  const image3 = "https://media-manager-c.questera.ai/greta-media/51a91a93706672e53ae444a23254479bc2f5b1a8165fab41d806a43d2b869fef8268d071e9340f739bfeb737c7b371ae/images/aW1hZ2UvcG5n/1db3688b73283a152a22b2aaaa34eab8.png";

  const amenitiesList = [
    { icon: FiWifi, text: "Fast Wi-Fi" },
    { icon: FiWind, text: "Air Conditioning" },
    { icon: FiCoffee, text: "Kitchen Space" },
    { icon: FiArchive, text: "Refrigerator" },
    { icon: FiCloudRain, text: "Rain Shower" },
    { icon: FiLock, text: "Private Bathroom" },
    { icon: FiHeart, text: "Pet Friendly" },
    { icon: FiTv, text: "Smart TV" },
  ];

  return (
    <PageTransition>
      {/* Header */}
      <div className="bg-brand-olive pt-20 pb-24 text-center px-4">
        <span className="font-script text-3xl text-brand-light mb-2 block">Accommodations</span>
        <h1 className="font-serif text-4xl md:text-5xl text-brand-cream uppercase tracking-wider">The Stay</h1>
      </div>

      <div className="container mx-auto px-6 max-w-6xl py-16">
        {/* Main Details */}
        <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
          <div>
            <h2 className="font-serif text-3xl text-brand-dark mb-6 uppercase tracking-wide">Elegance Meets Comfort</h2>
            <p className="text-brand-sage font-light leading-relaxed mb-6">
              R. VENUE Apartelle offers thoughtfully curated spaces that prioritize your comfort. Enjoy a restful night's sleep on premium mattresses wrapped in soft, high-quality linens. The interior features a harmonious blend of muted sage greens, warm woods, and crisp whites.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="border border-brand-light p-4 rounded-sm text-center">
                <span className="block font-serif text-2xl text-brand-olive">2-4</span>
                <span className="text-sm text-brand-sage uppercase tracking-wider">Guests</span>
              </div>
              <div className="border border-brand-light p-4 rounded-sm text-center">
                <span className="block font-serif text-2xl text-brand-olive">1</span>
                <span className="text-sm text-brand-sage uppercase tracking-wider">Bedroom</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 border border-brand-olive/10 rounded-sm translate-x-2 translate-y-2"></div>
            <img 
              src={image1} 
              alt="Cozy Bedroom" 
              className="relative z-10 w-full h-auto rounded-sm shadow-lg object-cover aspect-square md:aspect-[4/3]"
            />
          </div>
        </div>

        {/* Space Layout */}
        <div className="grid md:grid-cols-2 gap-16 items-center flex-col-reverse md:flex-row-reverse mb-24">
          <div>
            <h2 className="font-serif text-3xl text-brand-dark mb-6 uppercase tracking-wide">Generous Living Space</h2>
            <p className="text-brand-sage font-light leading-relaxed mb-6">
              Beyond the bedroom, guests enjoy a spacious open-concept area featuring modern seating, elegant flooring, and large windows that let in abundant natural light. It's the perfect spot to unwind after a day of exploring Lucban.
            </p>
            <ul className="space-y-3 mt-6">
              {['Dedicated seating area', 'Modern lighting fixtures', 'Clean, contemporary aesthetic', 'Seamless indoor flow'].map((item, i) => (
                <li key={i} className="flex items-center text-brand-dark font-light">
                  <SafeIcon icon={FiCheck} className="text-brand-olive mr-3 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 border border-brand-olive/10 rounded-sm -translate-x-2 -translate-y-2"></div>
            <img 
              src={image2} 
              alt="Spacious Living Area" 
              className="relative z-10 w-full h-auto rounded-sm shadow-lg object-cover aspect-square md:aspect-[4/3]"
            />
          </div>
        </div>

        {/* Balcony & Outdoor Living */}
        <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
          <div>
            <h2 className="font-serif text-3xl text-brand-dark mb-6 uppercase tracking-wide">Balcony & Outdoor Living</h2>
            <p className="text-brand-sage font-light leading-relaxed mb-6">
              Experience the soul of Lucban from our expansive balcony with an outdoor living space. This private sanctuary offers a breathtaking, unobstructed view of Mount Banahaw, while also overlooking the lively streets and the vibrant park at the heart of the town.
            </p>
            <ul className="space-y-3 mt-6">
              {['Panoramic views of Mount Banahaw', 'Premium balcony with outdoor seating', 'Overlooks the town plaza and central streets', 'Ideal for soaking in the cool Lucban breeze'].map((item, i) => (
                <li key={i} className="flex items-center text-brand-dark font-light">
                  <SafeIcon icon={FiCheck} className="text-brand-olive mr-3 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 border border-brand-olive/10 rounded-sm translate-x-2 translate-y-2"></div>
            <img 
              src={image3} 
              alt="Balcony and Outdoor Living Space" 
              className="relative z-10 w-full h-auto rounded-sm shadow-lg object-cover aspect-square md:aspect-[4/3]"
            />
          </div>
        </div>

        {/* Unified Amenities Section */}
        <div className="bg-white p-12 rounded-sm shadow-sm border border-brand-light/30">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-brand-dark mb-4 uppercase tracking-wide">Apartelle Amenities</h2>
            <p className="text-brand-sage font-light">Thoughtfully selected for a premium guest experience.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-8">
            {amenitiesList.map((amenity, index) => (
              <div key={index} className="flex flex-col items-center text-center group">
                <div className="w-20 h-20 bg-brand-cream rounded-full flex items-center justify-center mb-6 transition-all duration-300 border border-brand-light/20 group-hover:bg-brand-olive group-hover:scale-110 group-hover:shadow-lg">
                  <SafeIcon 
                    icon={amenity.icon} 
                    className="text-3xl text-brand-olive transition-colors duration-300 group-hover:text-white" 
                  />
                </div>
                <span className="text-brand-dark font-bold text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 group-hover:text-brand-olive">
                  {amenity.text}
                </span>
              </div>
            ))}
          </div>
          
          <div className="mt-16 pt-10 border-t border-brand-light/20 flex flex-col md:flex-row items-center justify-center gap-8 text-brand-sage">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-cream rounded-sm">
                <SafeIcon icon={FiShield} className="text-brand-olive" />
              </div>
              <span className="text-xs uppercase tracking-widest font-medium">Secure 3rd Floor Access</span>
            </div>
            <div className="hidden md:block w-px h-8 bg-brand-light/30"></div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-cream rounded-sm">
                <SafeIcon icon={FiMaximize} className="text-brand-olive" />
              </div>
              <span className="text-xs uppercase tracking-widest font-medium">Open-Concept Living</span>
            </div>
          </div>
        </div>

      </div>
    </PageTransition>
  );
};

export default Stay;